import { setupScrollReveal } from '../util/scrollReveal.js'
import { getLang } from '../util/i18n.js'
import { getHeroTexts } from '../data/heroTexts.js'

const HERO_VIDEO_URL =
  'https://samtc.oss-cn-hangzhou.aliyuncs.com/video/hero_v2.mp4'

class SamtHero extends HTMLElement {
  connectedCallback() {
    this.render()
    this.initHeroOverlay()
    this.initHeroVisibility()
    // 初始化公司介绍段落的滚动显现动画
    this.initHeroCompanyReveal()
  }

  render() {
    const lang = getLang()
    const t = getHeroTexts(lang)

    this.innerHTML = `
      <section class="home-hero relative flex h-auto w-screen flex-col">
        <div
          id="hero-overlay"
          class="bg-samt-mask pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity duration-300"
        ></div>
        <!-- fixed hero -->
        <div id="hero-fixed" class="fixed inset-0 -z-[1] h-screen w-screen pointer-events-none">
          <video
            id="hero-video"
            src="${HERO_VIDEO_URL}"
            class="absolute inset-0 h-full w-full object-cover -z-1"
            autoplay
            muted
            loop
            playsinline
            preload="auto"
          ></video>
        </div>
        <!-- END fixed hero -->

        <!-- hero text -->
        <div
          id="hero-text-block-1"
          class="relative z-20 flex h-screen w-screen flex-col items-start justify-end gap-4 px-2 pb-12 text-2xl lg:px-20 lg:text-6xl"
        >
          <div class="flex w-full flex-wrap gap-y-4 lg:w-7/10">
            <span class="space-x-4"
              >${t.values}</span
            >
          </div>
          <hr class="mt-16 w-full text-gray-400/55" />
        </div>
        <!-- END hero text -->

        <!-- hero text -->
        <div
          id="hero-text-company"
          class="hero-company-text relative z-20 flex h-[80vh] w-screen flex-col items-start justify-between gap-6 px-2 pb-8 text-2xl lg:flex-row lg:px-20 lg:text-6xl"
        >
          <div class="flex h-full w-full items-center text-lg/6 lg:w-2/5">
            <span
              data-scroll-reveal="scoped"
              class="space-x-4 text-2xl lg:text-3xl"
              >${t.companyTitle}</span
            >
          </div>
          <div
            class="flex h-full w-full flex-wrap items-center text-lg lg:w-3/5 lg:text-3xl 2xl:text-4xl"
          >
            <span data-scroll-reveal="scoped"
              >${t.companyDesc}</span
            >
          </div>
        </div>
        <!-- END hero text -->
      </section>
    `

    // 外链视频直接使用绝对地址，不走 new URL(import.meta.url) 解析
    const heroVideo = this.querySelector('#hero-video')
    if (heroVideo) {
      heroVideo.src = HERO_VIDEO_URL
      heroVideo.load()
      heroVideo.play().catch((e) => {
        console.warn('[SamtHero] video 自动播放失败：', e)
      })
    }
  }

  initHeroOverlay() {
    const overlay = document.getElementById('hero-overlay')
    const target = document.getElementById('hero-text-block-1')

    if (!overlay || !target) return

    function smoothstep(t) {
      t = Math.max(0, Math.min(1, t))
      return t * t * (3 - 2 * t)
    }

    let ticking = false

    function updateOverlay() {
      const rect = target.getBoundingClientRect()
      const vh = window.innerHeight

      const visibleTop = Math.max(0, rect.top)
      const visibleBottom = Math.min(vh, rect.bottom)
      const visibleHeight = Math.max(0, visibleBottom - visibleTop)
      const progress = rect.height > 0 ? visibleHeight / rect.height : 0

      const eased = smoothstep(progress)
      const minOpacity = 0.3
      const maxOpacity = 0.65
      const opacity = minOpacity + (maxOpacity - minOpacity) * (1 - eased)
      overlay.style.opacity = opacity.toFixed(2)

      ticking = false
    }

    function requestTick() {
      if (!ticking) {
        requestAnimationFrame(updateOverlay)
        ticking = true
      }
    }

    window.addEventListener('scroll', requestTick)
    window.addEventListener('resize', requestTick)
    // 初始计算
    requestTick()
  }

  initHeroVisibility() {
    const host = this
    const fixed = this.querySelector('#hero-fixed')
    if (!fixed) return

    function applyHidden(hidden) {
      if (hidden) fixed.classList.add('hidden')
      else fixed.classList.remove('hidden')
    }

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          const e = entries[0]
          applyHidden(e.intersectionRatio <= 0.05)
        },
        {
          threshold: [0, 0.05, 0.1, 0.25, 1]
        }
      )
      io.observe(host)
      this._ioHeroVisibility = io
    } else {
      const update = () => {
        const rect = host.getBoundingClientRect()
        const vh = window.innerHeight
        const visible = rect.bottom > 0 && rect.top < vh
        applyHidden(!visible)
      }
      window.addEventListener('scroll', update)
      window.addEventListener('resize', update)
      update()
      this._fallbackUpdateHeroVisibility = update
    }
  }

  initHeroCompanyReveal() {
    // 使用通用 scrollReveal 辅助，保持与其他段落一致的显现逻辑
    const container = this.querySelector('#hero-text-company')
    if (!container) return

    const cleanup = setupScrollReveal(container, {
      selector: '[data-scroll-reveal="scoped"]',
      visibleClass: 'scroll-reveal-visible',
      stagger: 120,
      guardForward: false
    })

    if (cleanup) this._cleanupCompanyReveal = cleanup
  }

  disconnectedCallback() {
    if (this._ioHeroVisibility) this._ioHeroVisibility.disconnect()
    if (this._cleanupCompanyReveal) {
      this._cleanupCompanyReveal()
      this._cleanupCompanyReveal = null
    }

    if (this._fallbackUpdateHeroVisibility) {
      window.removeEventListener('scroll', this._fallbackUpdateHeroVisibility)
      window.removeEventListener('resize', this._fallbackUpdateHeroVisibility)
      this._fallbackUpdateHeroVisibility = null
    }
  }
}

customElements.define('samt-hero', SamtHero)
